import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Sparkles, Lightbulb, CheckCircle, Settings, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useRefurbishmentEstimate } from "@/hooks/useRefurbishmentEstimate";
import { useAppliance } from "@/hooks/useAppliance";
import { useTranslation } from "react-i18next";
import { useWorkflowRunner } from "@/workflows/runnerContext";

interface AssessmentAnswer {
  question: string;
  category: string;
  isCritical: boolean;
  weight: string;
  answer: string;
  description: string;
  score: number;
  maxScore: number;
}

const assessmentResults: AssessmentAnswer[] = [
  { question: "What is the approximate age of this appliance?", category: "Age & Model Assessment", isCritical: true, weight: "2x", answer: "3-5 years", description: "Recent model, parts readily available", score: 9, maxScore: 10 },
  { question: "What is the overall structural condition of the appliance?", category: "Structural Assessment", isCritical: true, weight: "3x", answer: "Good", description: "Minor cosmetic issues only", score: 8, maxScore: 10 },
  { question: "How would you assess the electrical system condition?", category: "Electrical Assessment", isCritical: true, weight: "3x", answer: "Minor Issues", description: "Some buttons or lights not working", score: 7, maxScore: 10 },
  { question: "What is the condition of mechanical components (drum, bearings, pump)?", category: "Mechanical Assessment", isCritical: false, weight: "2.5x", answer: "Good", description: "Minor wear but functional", score: 8, maxScore: 10 },
  { question: "Assess the condition of seals, gaskets, and hoses:", category: "Seals & Hoses", isCritical: false, weight: "2x", answer: "Minor Wear", description: "Slight discoloration but intact", score: 8, maxScore: 10 },
];

const RefurbishmentResults = () => {
  const { t } = useTranslation("refurb");
  const { t: tc } = useTranslation("common");
  const { id } = useParams();
  const navigate = useNavigate();
  const runner = useWorkflowRunner();

  const { appliance: loadedAppliance, notFound } = useAppliance(id);

  useEffect(() => {
    if (notFound) {
      toast.error(t("common.applianceNotFound"));
      navigate("/work-orders");
    }
  }, [notFound, navigate, t]);

  const appliance = loadedAppliance ?? {
    name: t("common.loading"),
    modelNumber: "",
    category: "Appliance",
    brand: "",
  };

  const { estimatedCost: liveEstimatedCost, laborCost, materialCost } = useRefurbishmentEstimate(
    appliance.brand, appliance.category, [], appliance.modelNumber,
  );

  const overallScore = 79;
  const criticalIssues = 0;
  const categoriesAssessed = 5;
  const estimatedCost = liveEstimatedCost > 0 ? Math.round(liveEstimatedCost) : 128;
  const resaleValue = 420;
  const grossMargin = resaleValue - estimatedCost;
  const costRatio = resaleValue > 0 ? Math.round((estimatedCost / resaleValue) * 100) : 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-6 py-4 bg-card border-b border-border">
        <div className="flex items-center gap-6">
          <button onClick={() => navigate("/work-orders")} className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{t("common.backToWorkOrders")}</span>
          </button>
          <div className="flex items-center gap-2">
            <div>
              <h1 className="font-bold text-foreground">FixID</h1>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <RefreshCw className="w-3 h-3" />
                <span>{t("results.subtitleNav")}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="font-medium text-foreground">{appliance.name}</p>
            <div className="flex items-center gap-2 justify-end">
              <span className="text-sm text-muted-foreground">{appliance.modelNumber}</span>
              <span className="category-badge category-badge-washing text-xs">{appliance.category}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg hover:bg-muted transition-colors"><Settings className="w-5 h-5 text-muted-foreground" /></button>
            <button className="p-2 rounded-lg hover:bg-muted transition-colors"><User className="w-5 h-5 text-muted-foreground" /></button>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-5xl mx-auto">
        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">{t("results.title")}</h2>
              <p className="text-muted-foreground">{[appliance.brand, appliance.modelNumber].filter(Boolean).join(" ")}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="bg-card rounded-2xl border border-border p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-primary" />
              <h3 className="font-semibold text-foreground">{t("results.conditionScore")}</h3>
            </div>
            <div className="text-center mb-4">
              <p className="text-5xl font-bold text-foreground">{overallScore}%</p>
              <p className="text-sm text-muted-foreground">{t("results.overallCondition")}</p>
            </div>
            <Progress value={overallScore} className="h-3 mb-4" />
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between p-2 bg-background rounded-lg">
                <span className="text-muted-foreground">{criticalIssues}</span>
                <span className="text-foreground">{t("results.criticalIssues")}</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-background rounded-lg">
                <span className="text-muted-foreground">{categoriesAssessed}</span>
                <span className="text-foreground">{t("results.categoriesAssessed")}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 bg-card rounded-2xl border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-repair" />
                <h3 className="font-semibold text-foreground">{t("results.economicAnalysis")}</h3>
              </div>
              <RefreshCw className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="grid grid-cols-3 gap-3 mb-2">
              <div className="bg-destructive/10 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-destructive">€{estimatedCost}</p>
                <p className="text-xs text-muted-foreground">{t("results.estimatedCost")}</p>
              </div>
              <div className="bg-muted rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-foreground">€{resaleValue}</p>
                <p className="text-xs text-muted-foreground">{t("results.resaleValue")}</p>
              </div>
              <div className="bg-refurbish/10 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-refurbish">€{grossMargin}</p>
                <p className="text-xs text-muted-foreground">{t("results.grossMargin")}</p>
              </div>
            </div>
            {(laborCost > 0 || materialCost > 0) && (
              <p className="text-xs text-muted-foreground text-center mb-4">
                {t("results.laborMaterials", { labor: Math.round(laborCost), materials: Math.round(materialCost) })}
              </p>
            )}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">{t("results.costRatio")}</span>
                <span className="text-sm font-medium text-foreground">{t("results.costRatioPct", { pct: costRatio })}</span>
              </div>
              <Progress value={costRatio} className="h-2 bg-muted [&>div]:bg-foreground" />
              <p className="text-xs text-muted-foreground mt-1">{t("results.costRatioHelp")}</p>
            </div>
            <div className="bg-refurbish/5 border border-refurbish/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-refurbish" />
                <span className="font-semibold text-foreground">{t("results.proceedTitle")}</span>
                <span className="px-2 py-0.5 bg-refurbish/20 text-refurbish text-xs rounded-full">{t("results.highConfidence")}</span>
              </div>
              <p className="font-medium text-foreground mb-1">{t("results.excellentCandidate")}</p>
              <p className="text-sm text-muted-foreground">{t("results.proceedDesc")}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => (runner ? runner.back() : navigate(`/appliance/${id}/refurbish`))}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("results.adjustAssessment")}
            </Button>
            <Button onClick={() => (runner ? runner.complete() : navigate(`/appliance/${id}/refurbish/start`))} className="bg-refurbish hover:bg-refurbish/90 text-refurbish-foreground">
              <RefreshCw className="w-4 h-4 mr-2" />
              {t("results.startRefurbishment")}
            </Button>
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <h3 className="font-semibold text-foreground mb-4">{t("results.assessmentDetails")}</h3>
          <div className="space-y-4">
            {assessmentResults.map((result, index) => (
              <div key={index} className="bg-background rounded-xl border border-border p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="font-medium text-foreground mb-1">{result.question}</p>
                    <span className="inline-block px-2 py-0.5 bg-foreground text-background text-xs rounded-full">
                      {result.category}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {result.isCritical && (
                      <span className="px-2 py-0.5 bg-destructive text-destructive-foreground text-xs rounded-full">
                        {t("results.critical")}
                      </span>
                    )}
                    <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                      {t("results.weight", { w: result.weight })}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div>
                    <p className="font-semibold text-foreground">{result.answer}</p>
                    <p className="text-sm text-muted-foreground">{result.description}</p>
                  </div>
                  <div className="flex items-center gap-1 text-refurbish">
                    <span className="font-medium">{t("results.score", { score: result.score, max: result.maxScore })}</span>
                    <CheckCircle className="w-4 h-4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RefurbishmentResults;
